import React, { useMemo } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import TitleCase from 'components/formatting/TitleCase';
import HorizontalBar from 'components/charting/HorizontalBar';
import { DashboardQueryDrilldownDialog } from 'components/dialogs/QueryDrilldown/DashboardQueryDrilldownDialog';
import { Query } from 'system/query/Query';
import { CategoryWidgetData, MakeCategorySegmentsFromSingleResponse, MakeCategorySegmentsFromMultiResponses } from 'components/widgets/utility/CategoryChartsAPIResponseHandlers';
import { defaultSettings } from './CategoryBarsSettings';
import { getMaximumDisplayCountOptions } from '../CategoryArcs/CategoryArcsSettings';
import WidgetTitle from 'components/widgetParts/WidgetTitle';
import WidgetAlert, { WidgetErrorType } from 'components/widgetParts/WidgetAlert';
import WidgetLayout from 'components/widgetParts/WidgetLayout';
import ISOPercentage from 'components/formatting/ISOPercentage';
import { useTranslation } from 'react-i18next';
import withQueryWidget, { WithQueryWidgetProps } from 'components/widgetParts/queryWidgetComposition/withQueryWidget';
import { getSingleWidgetResponseByName, getWidgetResponsesByName, widgetQueryTitleByResponses } from 'system/widget/Widget';
import BarChartTooltip from '../BarChart/BarChartTooltip';
import ISONumber from 'components/formatting/ISONumber';

function CategoryBarsContent(props: WithQueryWidgetProps): JSX.Element {
  const theme = useTheme();
  const { i18n } = useTranslation();
  
  const [drilldownTitle, setDrilldownTitle] = React.useState<string | null>(null);
  const [drilldownQuery, setDrilldownQuery] = React.useState<Query | null>(null);
  const [drilldownQueryIds, setDrilldownQueryIds] = React.useState<string[]>([]);
  const apiResponseInfo = getSingleWidgetResponseByName(props.apiResponses, "category-bars");
  const [apiResponses, setApiResponses] = React.useState(getWidgetResponsesByName(props.apiResponses, "category-bars"));

  const categoryData:CategoryWidgetData | undefined = useMemo(() => {
    setApiResponses(getWidgetResponsesByName(props.apiResponses, "category-bars"));
    if (!apiResponses) {
      return undefined;
    }

    // If there are multiple responses, or if the widget is set to always show multi-template-queries,
    // then we treat this as a multi-query category bar chart
    if (Array.isArray(apiResponses) && apiResponses.length > 1) {
      return MakeCategorySegmentsFromMultiResponses(apiResponses, props.queryInfo, theme, i18n);
    } else {
      let apiResponse = apiResponses;
      if (Array.isArray(apiResponses)) {
        if (apiResponses.length === 0) {
          return undefined;
        }
        apiResponse = apiResponses[0];
      }
      // Single response
      // We assume that the response is for a single query, so we can directly pass
      return MakeCategorySegmentsFromSingleResponse(apiResponse, props.queryInfo, theme);
    }
  }, [props.apiResponses, props.queryInfo, props.widget, theme]);

  const currentState = {...defaultSettings, ...props.widget?.data?.settings};

  const displayCountOptions = getMaximumDisplayCountOptions(i18n);
  const selectedDisplayCount = displayCountOptions.find((option) => option.value === currentState.maxDisplayed);
  const displayCount = Math.min(selectedDisplayCount?.value ?? defaultSettings.maxDisplayed, categoryData?.categoryRows?.length ?? 0);
  const isMultiQuery = Array.isArray(props.activeWidgetRequests[0]) && props.activeWidgetRequests[0].length > 1;

  const sortedRows = useMemo(() => {
    const rows = [...(categoryData?.categoryRows ?? [])];

    if (isMultiQuery) {
      const sortValueFor = (row: { percent?: number; count?: number }) =>
        currentState.displayValue === "count" ? (row.count ?? 0) : (row.percent ?? 0);

      switch (currentState.sortValue) {
        case "asc":
          rows.sort(
            (a, b) =>
              sortValueFor(a) - sortValueFor(b) ||
              (a.label ?? "").localeCompare(b.label ?? "")
          );
          break;
        case "desc":
          rows.sort(
            (a, b) =>
              sortValueFor(b) - sortValueFor(a) ||
              (a.label ?? "").localeCompare(b.label ?? "")
          );
          break;
        case "none":
        default:
          break;
      }
    }

    return rows.slice(0, displayCount);
  }, [categoryData?.categoryRows, currentState.sortValue, currentState.displayValue, displayCount]);

  const displayRows = useMemo(() => {
    return sortedRows.map((row, index) => {
      const drilldown = () => {
        if (row.drilldownQuery) {
          setDrilldownQuery(row.drilldownQuery);
          setDrilldownTitle(row.label);
          if (apiResponses && apiResponses.length > 1) {
            const originatingResponse = apiResponses.find(r => r.queryDisplayTitle === row.queryLabel);
            setDrilldownQueryIds(originatingResponse?.originatingRequest?.activeQueryIds || []);
          } else {
            setDrilldownQueryIds(apiResponseInfo?.originatingRequest?.activeQueryIds || []);
          }
        }
      };

      const bar = {
        color: row.color || theme.palette.primary.main,
        count: row.count,
        drilldownQuery: row.drilldownQuery || null,
        key: row.label,
        label: row.label,
        category_label: i18n.t("records"),
        percent: row.percent,
      };

      return (
        <Box
          key={`${row.key || row.label || "row"}-${index}`}
          sx={{ cursor: "pointer" }}
          onClick={drilldown}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="subtitle2">
              <TitleCase text={bar.label} />
            </Typography>
            <Box sx={{ ml: 1 }}>
              {currentState.displayValue === "percent" && <ISOPercentage value={bar.percent} />}
              {currentState.displayValue === "count" && <ISONumber number={bar.count} />}
            </Box>
          </Box>
          <HorizontalBar
            percentage={bar.percent ?? 0}
            sx={{ mb: 2 }}
            color={bar.color}
            bar={bar}
            tooltip={(bar) => <BarChartTooltip bar={bar} index={index} color={bar.color} />}
          />
        </Box>
      );
    });
  }, [categoryData?.categoryRows, currentState.displayValue, displayCount, apiResponses, apiResponseInfo, theme, i18n]);

  const widgetQueryTitle = widgetQueryTitleByResponses(props.apiResponses, "category-bars", i18n);

  return (
    <>
      {displayRows.length !== 0 &&
        <WidgetLayout
          header={
            <WidgetTitle
              alignment={currentState.widgetTitleAlignment}
              customTitle={currentState.customWidgetTitle}
              defaultTitle={categoryData?.fieldLabel || " "}
              settingsTitle={currentState.widgetTitle}
              widgetQueryTitle={widgetQueryTitle}
              />
          }
        >
          <Box sx={{overflowY: "auto", p: 2, pt: 1}}>{displayRows}</Box>
          {drilldownQuery && drilldownTitle && drilldownQueryIds &&
            <DashboardQueryDrilldownDialog
              title={drilldownTitle}
              queryInfo={props.queryInfo}
              onClose={() => setDrilldownQuery(null)}
              activeQueryIds={drilldownQueryIds}
              drilldownQuery={drilldownQuery}
              widgetRequestInfo={props.widget?.request_info} />
          }
        </WidgetLayout>
      }
      {displayRows.length === 0 &&
        <WidgetAlert type={WidgetErrorType.NO_DATA} />
      }
    </>
  );
}

export default withQueryWidget(CategoryBarsContent, {
  hasSettingsDialog: true,
  hasQueryDialog: true
});